import Dialog from "../Dialog";
import MUITypography from "@mui/material/Typography";
import MUITable from "@mui/material/Table";
import MUITableBody from "@mui/material/TableBody";
import MUITableCell from "@mui/material/TableCell";
import MUITableContainer from "@mui/material/TableContainer";
import MUITableHead from "@mui/material/TableHead";
import MUITableRow from "@mui/material/TableRow";
import MUIPaper from "@mui/material/Paper";
import MUIButton from "@mui/material/Button";
import MUIDialogActions from "@mui/material/DialogActions";
import MUITextField from "@mui/material/TextField";
import MUIInputAdornment from "@mui/material/InputAdornment";
import MUIDownloadIcon from "@mui/icons-material/Download";
import MUIInput from "@mui/material/Input";

import * as Sentry from "@sentry/browser";
import Progress from "../Progress";

export default class DialogPatientResponsibilityEstimator extends Dialog {
  constructor(props) {
    super(props);

    this.currencyFormatter = new Intl.NumberFormat("en-US", {
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    this.state = {
      rows: props.rows,
      loading: false,
      isSaving: false,
      copayPercentage: null,
      deductible: this.props.deductible != null ? this.props.deductible : null,
      deductibleInput:
        this.props.deductible != null
          ? this.currencyFormatter.format(this.props.deductible)
          : "",
      coInsurance: this.props.coInsurance,
      coInsuranceInput:
        this.props.coInsurance != null
          ? this.currencyFormatter.format(this.props.coInsurance)
          : "",
    };
  }

  formatCurrency(amount) {
    return this.currencyFormatter.format(amount);
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(prevProps.rows) !== JSON.stringify(this.props.rows) ||
      prevProps.rows.length !== this.props.rows.length
    ) {
      this.setState({
        rows: this.props.rows,
      });
    }
    if (prevProps.loading !== this.props.loading) {
      this.setState({
        loading: this.props.loading,
      });
    }
    if (prevProps.isSaving !== this.props.isSaving) {
      this.setState({
        isSaving: this.props.isSaving,
      });
    }
    if (prevProps.deductible !== this.props.deductible) {
      this.setState({
        deductible: this.props.deductible,
        deductibleInput:
          this.props.deductible != null
            ? this.formatCurrency(this.props.deductible)
            : "",
      });
    }

    if (prevProps.coInsurance !== this.props.coInsurance) {
      this.setState({
        coInsurance: this.props.coInsurance,
        coInsuranceInput:
          this.props.coInsurance != null
            ? this.formatCurrency(this.props.coInsurance)
            : "",
      });
    }
  }

  /**
   * Render header title of dialog
   */
  renderHeader() {
    return (
      <MUITypography variant="h6" color="textPrimary">
        Patient Responsibility Estimator
      </MUITypography>
    );
  }

  /**
   * Render cancel and confirm button actions of dialog.
   */
  renderActions() {
    return (
      <MUIDialogActions spacing={2}>
        <MUIButton variant="text" onClick={this.props.onClose}>
          Cancel
        </MUIButton>
        <MUIButton
          variant="outlined"
          loading={this.props.isUpdatingChart}
          disabled={
            this.props.isUpdatingChart ||
            this.state.rows.some((row) => row.amount === null) ||
            (this.props.order?.insuranceTypeId !== 4 &&
              this.state.deductible === null) ||
            (this.props.order?.insuranceTypeId !== 4 &&
              this.state.coInsurance === null)
          }
          startIcon={<MUIDownloadIcon />}
          onClick={() => this.handleApplyChanges(true)}
        >
          {/* If no insurnace or insurance type is Cash, show "Good Faith Estimate", otherwise show "Estimate" */}
          {!this.props.order?.insuranceTypeId ||
          this.props.order?.insuranceTypeId === 4
            ? "Good Faith Estimate"
            : "Estimate"}
        </MUIButton>
        <MUIButton
          variant="contained"
          disabled={
            this.props.isUpdatingChart ||
            this.state.rows.some((row) => row.amount === null) ||
            (this.props.order?.insuranceTypeId !== 4 &&
              this.state.deductible === null) ||
            (this.props.order?.insuranceTypeId !== 4 &&
              this.state.coInsurance === null)
          }
          loading={this.props.isUpdatingChart}
          onClick={() => {
            this.handleApplyChanges(false);
          }}
        >
          Apply
        </MUIButton>
      </MUIDialogActions>
    );
  }

  /**
   * Render main content text of dialog
   */
  renderContent() {
    if (this.state.loading) {
      return <Progress />;
    }

    return (
      <>
        <MUITableContainer component={MUIPaper} elevation={1}>
          <MUITypography
            variant="subtitle2"
            sx={{ padding: 2, fontSize: "1.1rem" }}
          >
            Charges
          </MUITypography>
          <MUITable stickyHeader size="small">
            <MUITableHead>
              <MUITableRow>
                <MUITableCell sx={{ width: "125px" }}>HCPCS</MUITableCell>
                <MUITableCell>Product Name</MUITableCell>
                <MUITableCell>Qty</MUITableCell>
                <MUITableCell sx={{ width: "120px" }} align="right">
                  Unit Amount
                </MUITableCell>
                <MUITableCell sx={{ width: "120px" }} align="right">
                  Total
                </MUITableCell>
              </MUITableRow>
            </MUITableHead>
            <MUITableBody>
              {this.state.rows.map((row, index) => {
                return (
                  <MUITableRow key={index}>
                    <MUITableCell component="th" scope="row">
                      {`${row.hcpcs?.code || ""}${row.modifierCode?.code ? ` ${row.modifierCode.code}` : "N/A"}`}
                    </MUITableCell>
                    <MUITableCell>{row.productSubTypeName || ""}</MUITableCell>
                    <MUITableCell>{row.qty || ""}</MUITableCell>
                    <MUITableCell align="right">
                      {row?.feeSchedule === null ? (
                        <MUIInput
                          value={
                            row?.formatted
                              ? row?.formatted
                              : row?.amount !== null
                                ? this.formatCurrency(row?.amount)
                                : ""
                          }
                          inputProps={{
                            maxLength: 255,
                            style: { textAlign: "right" },
                          }}
                          sx={{ height: "25px" }}
                          onChange={(e) => this.handleFeeInputChange(e, index)}
                          onBlur={(e) => this.handleFreeInputBlur(e, index)}
                          startAdornment={
                            <MUIInputAdornment position="start">
                              $
                            </MUIInputAdornment>
                          }
                        />
                      ) : (
                        this.formatCurrency(row.amount)
                      )}
                    </MUITableCell>
                    <MUITableCell align="right">
                      {row?.amount != null
                        ? this.formatCurrency(row?.amount * row.qty)
                        : null}
                    </MUITableCell>
                  </MUITableRow>
                );
              })}
              <MUITableRow>
                <MUITableCell>
                  <strong style={{ whiteSpace: "nowrap" }}>
                    {this.props.order?.insuranceTypeId !== 4
                      ? "Total Charges"
                      : "Estimated Patient Responsibility"}
                  </strong>
                </MUITableCell>
                <MUITableCell colSpan={3}></MUITableCell>
                <MUITableCell align="right">
                  <strong>
                    {this.formatCurrency(
                      this.state.rows.reduce(
                        (sum, row) => sum + (row?.amount || 0) * (row.qty || 0),
                        0,
                      ),
                    )}
                  </strong>
                </MUITableCell>
              </MUITableRow>
            </MUITableBody>
          </MUITable>

          {this.props.order?.insuranceTypeId !== 4 ? (
            <>
              <MUITypography
                variant="subtitle2"
                sx={{ px: 2, pb: 0, pt: 3, fontSize: "1.1rem" }}
              >
                Patient Responsibility
              </MUITypography>
              <MUITable stickyHeader size="small">
                <MUITableHead>
                  <MUITableRow
                    sx={{
                      borderBottom: "none",
                      backgroundColor: "transparent !important",
                    }}
                  >
                    <MUITableCell
                      sx={{
                        borderBottom: "none",
                        backgroundColor: "transparent !important",
                      }}
                    ></MUITableCell>
                    <MUITableCell
                      sx={{
                        borderBottom: "none",
                        backgroundColor: "transparent !important",
                      }}
                    ></MUITableCell>
                    <MUITableCell
                      sx={{
                        width: "80px",
                        borderBottom: "none",
                        backgroundColor: "transparent !important",
                      }}
                    ></MUITableCell>
                    <MUITableCell
                      sx={{
                        width: "120px",
                        borderBottom: "none",
                        backgroundColor: "transparent !important",
                      }}
                      align="right"
                    ></MUITableCell>
                  </MUITableRow>
                </MUITableHead>
                <MUITableBody>
                  <MUITableRow>
                    <MUITableCell>Remaining Deductible</MUITableCell>
                    <MUITableCell colSpan={2}></MUITableCell>
                    <MUITableCell align="right">
                      <MUIInput
                        value={this.state.deductibleInput ?? ""}
                        inputProps={{
                          maxLength: 255,
                          style: { textAlign: "right" },
                        }}
                        sx={{ height: "25px" }}
                        onChange={(e) => {
                          let input = e.target.value;

                          if (input === "") {
                            this.setState({
                              deductible: null,
                              deductibleInput: "",
                            });
                            return;
                          }

                          let rawValue = input.replace(/,/g, "");

                          if (
                            rawValue.length > 1 &&
                            rawValue.startsWith("0") &&
                            rawValue[1] !== "."
                          ) {
                            rawValue = rawValue.slice(1);
                          }

                          // Only allow up to 5 digits before decimal, 2 after
                          const validFormat = /^\d{0,5}(\.\d{0,2})?$/;
                          if (!validFormat.test(rawValue)) return;

                          const parsed = parseFloat(rawValue);
                          const deductible = !isNaN(parsed) ? parsed : null;

                          // Split into integer and decimal parts
                          const [integerPart = "", decimalPart = ""] =
                            rawValue.split(".");

                          // Add commas to integer part
                          let formatted = integerPart.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ",",
                          );

                          // Reattach decimal if needed
                          if (input.endsWith(".") || decimalPart) {
                            formatted += "." + decimalPart;
                          }

                          this.setState({
                            deductible: deductible,
                            deductibleInput: formatted,
                          });
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value.replace(/,/g, "");

                          if (rawValue === "") {
                            this.setState({
                              deductible: null,
                              deductibleInput: "",
                            });
                            return;
                          }

                          const parsed = parseFloat(rawValue);

                          if (isNaN(parsed)) return;

                          const rounded = parsed.toFixed(2);
                          const [integerPart, decimalPart] = rounded.split(".");
                          let formatted =
                            integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
                            "." +
                            decimalPart;

                          this.setState({
                            deductible: parseFloat(rounded),
                            deductibleInput: formatted,
                          });
                        }}
                        startAdornment={
                          <MUIInputAdornment position="start">
                            $
                          </MUIInputAdornment>
                        }
                      />
                    </MUITableCell>
                  </MUITableRow>
                  <MUITableRow>
                    <MUITableCell>Coinsurance</MUITableCell>
                    <MUITableCell></MUITableCell>
                    <MUITableCell>
                      <MUITextField
                        variant="standard"
                        // Gives full control over what can be entered
                        type="text"
                        size="small"
                        value={this.state.copayPercentage || ""}
                        onBeforeInput={(e) => {
                          if (!/^\d$/.test(e.data)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = Math.min(
                            100,
                            Math.max(0, Number(e.target.value) || 0),
                          );

                          const calculatedCoInsuranceRaw =
                            value === 0 || value === null
                              ? null
                              : Math.max(
                                  this.state.rows.reduce(
                                    (sum, row) =>
                                      sum + (row.amount || 0) * (row.qty || 0),
                                    0,
                                  ) - (this.state.deductible || 0),
                                  0,
                                ) *
                                ((value || 0) / 100);

                          const calculatedCoInsurance =
                            calculatedCoInsuranceRaw === null
                              ? null
                              : calculatedCoInsuranceRaw;
                          const calculatedCoInsuranceInput =
                            calculatedCoInsuranceRaw === null
                              ? ""
                              : this.formatCurrency(calculatedCoInsuranceRaw);

                          this.setState({
                            copayPercentage: value,
                            coInsurance: calculatedCoInsurance,
                            coInsuranceInput: calculatedCoInsuranceInput,
                          });
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <MUIInputAdornment position="end">
                                %
                              </MUIInputAdornment>
                            ),
                          },

                          htmlInput: {
                            min: 0,
                            max: 100,
                            pattern: "[0-9]*",
                            style: { textAlign: "right" },
                          },

                          inputLabel: { shrink: true },
                        }}
                      />
                    </MUITableCell>
                    <MUITableCell align="right">
                      <MUIInput
                        value={
                          this.state.copayPercentage != null &&
                          this.state.copayPercentage !== 0
                            ? this.formatCurrency(
                                Math.max(
                                  this.state.rows.reduce(
                                    (sum, row) =>
                                      sum + (row.amount || 0) * (row.qty || 0),
                                    0,
                                  ) - (this.state.deductible || 0),
                                  0,
                                ) *
                                  ((this.state.copayPercentage || 0) / 100),
                              )
                            : this.state.coInsuranceInput != null
                              ? this.state.coInsuranceInput
                              : ""
                        }
                        disabled={
                          this.state.copayPercentage !== null &&
                          this.state.copayPercentage !== 0
                        }
                        inputProps={{
                          maxLength: 255,
                          style: { textAlign: "right" },
                        }}
                        sx={{ height: "25px" }}
                        onChange={(e) => {
                          let input = e.target.value;

                          if (input === "") {
                            this.setState({
                              coInsurance: null,
                              coInsuranceInput: null,
                            });
                            return;
                          }

                          let rawValue = input.replace(/,/g, "");

                          if (
                            rawValue.length > 1 &&
                            rawValue.startsWith("0") &&
                            rawValue[1] !== "."
                          ) {
                            rawValue = rawValue.slice(1);
                          }

                          // Only allow up to 5 digits before decimal, 2 after
                          const validFormat = /^\d{0,5}(\.\d{0,2})?$/;
                          if (!validFormat.test(rawValue)) return;

                          const parsed = parseFloat(rawValue);
                          const coInsurance = !isNaN(parsed) ? parsed : null;

                          // Split into integer and decimal parts
                          const [integerPart = "", decimalPart = ""] =
                            rawValue.split(".");

                          // Add commas to integer part
                          let formatted = integerPart.replace(
                            /\B(?=(\d{3})+(?!\d))/g,
                            ",",
                          );

                          // Reattach decimal if needed
                          if (input.endsWith(".") || decimalPart) {
                            formatted += "." + decimalPart;
                          }

                          this.setState({
                            coInsurance: coInsurance,
                            coInsuranceInput: formatted,
                          });
                        }}
                        onBlur={(e) => {
                          const rawValue = e.target.value.replace(/,/g, "");

                          if (rawValue === "") {
                            this.setState({
                              coInsurance: null,
                              coInsuranceInput: null,
                            });
                            return;
                          }

                          const parsed = parseFloat(rawValue);

                          if (isNaN(parsed)) return;

                          const rounded = parsed.toFixed(2);
                          const [integerPart, decimalPart] = rounded.split(".");
                          let formatted =
                            integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
                            "." +
                            decimalPart;

                          this.setState({
                            coInsurance: parseFloat(rounded),
                            coInsuranceInput: formatted,
                          });
                        }}
                        startAdornment={
                          <MUIInputAdornment position="start">
                            $
                          </MUIInputAdornment>
                        }
                      />
                    </MUITableCell>
                  </MUITableRow>
                  <MUITableRow>
                    <MUITableCell>
                      <strong>Estimated Patient Responsibility</strong>
                    </MUITableCell>
                    <MUITableCell colSpan={2} />
                    <MUITableCell align="right">
                      <strong>
                        {(() => {
                          const { patientResponsibility } =
                            this.calculatePatientResponsibility();
                          return this.formatCurrency(patientResponsibility);
                        })()}
                      </strong>
                    </MUITableCell>
                  </MUITableRow>
                </MUITableBody>
              </MUITable>
            </>
          ) : null}
        </MUITableContainer>
      </>
    );
  }

  handleApplyChanges(isDownloadGoodFaithEstimate) {
    const { coinsurance, deductible, patientResponsibility } =
      this.calculatePatientResponsibility();

    // Find rows where amount has changed
    const changedRows = this.state.rows.filter((stateRow, index) => {
      const originalRow = this.props.rows[index];
      if (!originalRow) {
        return false;
      }
      const stateAmount = stateRow.amount;
      const originalAmount = originalRow.amount;
      return stateAmount !== originalAmount;
    });

    this.props.onConfirm(
      coinsurance,
      deductible,
      patientResponsibility,
      changedRows,
      isDownloadGoodFaithEstimate,
    );
  }

  /**
   * Calculates the patient’s total amount owed after deductible and coinsurance.
   */
  calculatePatientResponsibility = () => {
    const totalCharges = this.state.rows.reduce(
      (sum, row) => sum + (row.amount || 0) * (row.qty || 0),
      0,
    );

    const deductibleAmount =
      this.state.deductible == null ? 0 : Number(this.state.deductible);
    const copayPercentage = this.state.copayPercentage || 0;

    let coinsurance;
    if (!copayPercentage) {
      coinsurance =
        this.state.coInsurance === null ? null : Number(this.state.coInsurance);
    } else {
      coinsurance =
        Math.max(totalCharges - deductibleAmount, 0) * (copayPercentage / 100);
    }

    const rawPatientResponsibility = deductibleAmount + (coinsurance || 0);

    const cappedResponsibility =
      this.props.order?.insuranceTypeId === 4
        ? totalCharges
        : Math.min(rawPatientResponsibility, totalCharges);
    return {
      totalCharges: Number(totalCharges.toFixed(2)),
      deductible:
        this.state.deductible == null
          ? null
          : Number(deductibleAmount.toFixed(2)),
      coinsurance: coinsurance === null ? null : Number(coinsurance.toFixed(2)),
      patientResponsibility: Number(cappedResponsibility.toFixed(2)),
    };
  };

  /**
   * Handles fee input change: parses, formats, and updates amount and formatted.
   */
  handleFeeInputChange(e, rowIndex) {
    let input = e.target.value;

    let rawValue = input.replace(/,/g, "");

    rawValue = rawValue.replace(/[^0-9.]/g, "");

    const parts = rawValue.split(".");
    if (parts.length > 2) {
      rawValue = parts[0] + "." + parts.slice(1).join("");
    }

    if (rawValue === "") {
      this.setState((prevState) => {
        const updatedRows = [...prevState.rows];
        updatedRows[rowIndex] = {
          ...updatedRows[rowIndex],
          amount: null,
          formatted: "",
        };
        return { rows: updatedRows };
      });
      return;
    }

    const [integerPartRaw, decimalPartRaw] = rawValue.split(".");

    const integerPart = integerPartRaw.slice(0, 5);

    const decimalPart = decimalPartRaw ? decimalPartRaw.slice(0, 2) : undefined;

    const cappedValue =
      decimalPart !== undefined ? `${integerPart}.${decimalPart}` : integerPart;

    const validPattern = /^(0|[1-9]\d{0,4})(\.\d{0,2})?$/;

    if (!validPattern.test(cappedValue)) {
      return;
    }

    const parsed = parseFloat(cappedValue);
    const amount = !isNaN(parsed) ? parsed : null;

    let formatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    if (decimalPart !== undefined) {
      formatted += `.${decimalPart}`;
    } else if (rawValue.endsWith(".")) {
      formatted += ".";
    }

    this.setState((prevState) => {
      const updatedRows = [...prevState.rows];
      updatedRows[rowIndex] = {
        ...updatedRows[rowIndex],
        amount: amount,
        formatted: formatted,
      };
      return { rows: updatedRows };
    });
  }

  /**
   * Handles blur on fee input: formats and rounds amount to two decimals.
   */
  handleFreeInputBlur(e, rowIndex) {
    const input = e.target.value;

    let rawValue = input.replace(/,/g, "").replace(/[^0-9.]/g, "");

    const parsed = parseFloat(rawValue);
    if (isNaN(parsed)) {
      return;
    }

    const roundedAmount = parseFloat(parsed.toFixed(2));
    const formatted = this.formatCurrency(roundedAmount);

    this.setState((prevState) => {
      const updatedRows = [...prevState.rows];
      updatedRows[rowIndex] = {
        ...updatedRows[rowIndex],
        amount: roundedAmount,
        formatted: formatted,
      };
      return { rows: updatedRows };
    });
  }

  /**
   * @returns {string} The max width of the dialog.
   */
  getMaxWidth() {
    return "md";
  }
}
